import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider
} from '@mui/material';
import {
  Settings as SettingsIcon,
  AttachMoney as CurrencyIcon,
  Percent as PercentIcon,
  Language as LanguageIcon,
  Receipt as ReceiptIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const MotionCard = motion(Card);
const MotionAccordion = motion(Accordion);

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const DefaultSettingsManager = ({ settings, updateDefaultSettings }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const [currentTab, setCurrentTab] = useState(0);

  const handleSettingUpdate = (key, value) => {
    if (typeof updateDefaultSettings !== 'function') {
      console.error('❌ updateDefaultSettings is not a function:', typeof updateDefaultSettings);
      return;
    }
    
    updateDefaultSettings({ [key]: value });
  };

  const defaultSettings = settings?.defaultSettings || {};

  const currencyOptions = [
    { value: '$', label: t('posConfig.currency.usd') || 'US Dollar ($)' },
    { value: '€', label: t('posConfig.currency.eur') || 'Euro (€)' },
    { value: '£', label: t('posConfig.currency.gbp') || 'British Pound (£)' },
    { value: '¥', label: t('posConfig.currency.jpy') || 'Japanese Yen (¥)' },
    { value: '₹', label: t('posConfig.currency.inr') || 'Indian Rupee (₹)' },
    { value: 'RM', label: t('posConfig.currency.myr') || 'Malaysian Ringgit (RM)' },
    { value: 'S$', label: t('posConfig.currency.sgd') || 'Singapore Dollar (S$)' }
  ];

  const languageOptions = [
    { value: 'en', label: t('posConfig.language.english') || 'English' },
    { value: 'ms', label: t('posConfig.language.malay') || 'Bahasa Malaysia' },
    { value: 'zh', label: t('posConfig.language.chinese') || '中文' },
    { value: 'ta', label: t('posConfig.language.tamil') || 'தமிழ்' }
  ];

  const settingSections = [
    {
      id: 'currency',
      label: 'Currency',
      icon: <CurrencyIcon />,
      color: '#10b981',
      content: (
        <Stack spacing={2}>
          <FormControl fullWidth size={isMobile ? "small" : "medium"}>
            <InputLabel>Currency Symbol</InputLabel>
            <Select
              value={defaultSettings.currencySymbol || '$'}
              onChange={(e) => handleSettingUpdate('currencySymbol', e.target.value)}
              label="Currency Symbol"
            >
              {currencyOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            type="number"
            label="Decimal Places"
            value={defaultSettings.decimalPlaces || 2}
            onChange={(e) => handleSettingUpdate('decimalPlaces', parseInt(e.target.value) || 2)}
            size={isMobile ? "small" : "medium"}
            inputProps={{ min: 0, max: 4 }}
            helperText="Number of decimal places for prices"
          />

          <FormControlLabel
            control={
              <Switch
                checked={defaultSettings.showCurrencySymbol !== false}
                onChange={(e) => handleSettingUpdate('showCurrencySymbol', e.target.checked)}
                color="primary"
              />
            }
            label="Show Currency Symbol"
          />

          <Box sx={{ mt: 2, p: 2, bgcolor: '#10b98108', borderRadius: 2, border: '1px solid #10b98120' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
              Preview:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#10b981' }}>
              {defaultSettings.showCurrencySymbol !== false ? defaultSettings.currencySymbol || '$' : ''}
              {(25.50).toFixed(defaultSettings.decimalPlaces || 2)}
            </Typography>
          </Box>
        </Stack>
      )
    },
    {
      id: 'tax',
      label: 'Tax Settings',
      icon: <PercentIcon />,
      color: '#f59e0b',
      content: (
        <Stack spacing={2}>
          <TextField
            fullWidth
            type="number"
            label="Default Tax Rate (%)"
            value={defaultSettings.defaultTaxRate || 0}
            onChange={(e) => handleSettingUpdate('defaultTaxRate', parseFloat(e.target.value) || 0)}
            size={isMobile ? "small" : "medium"}
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            helperText="Default tax percentage for items"
          />

          <FormControlLabel
            control={
              <Switch
                checked={defaultSettings.includeTaxInPrice || false}
                onChange={(e) => handleSettingUpdate('includeTaxInPrice', e.target.checked)}
                color="primary"
              />
            }
            label="Include Tax in Item Prices"
          />

          <FormControlLabel
            control={
              <Switch
                checked={defaultSettings.showTaxBreakdown || true}
                onChange={(e) => handleSettingUpdate('showTaxBreakdown', e.target.checked)}
                color="primary"
              />
            }
            label="Show Tax Breakdown on Receipt"
          />

          <Box sx={{ mt: 2, p: 2, bgcolor: '#f59e0b08', borderRadius: 2, border: '1px solid #f59e0b20' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
              Preview on $100:
            </Typography>
            <Typography variant="body2" sx={{ color: '#f59e0b', fontWeight: 600 }}>
              Tax: {defaultSettings.currencySymbol || '$'}
              {((100 * (defaultSettings.defaultTaxRate || 0)) / 100).toFixed(defaultSettings.decimalPlaces || 2)}
            </Typography>
          </Box>
        </Stack>
      )
    },
    {
      id: 'language',
      label: 'Language & Localization',
      icon: <LanguageIcon />,
      color: '#667eea',
      content: (
        <Stack spacing={2}>
          <FormControl fullWidth size={isMobile ? "small" : "medium"}>
            <InputLabel>Default Language</InputLabel>
            <Select
              value={defaultSettings.defaultLanguage || 'en'}
              onChange={(e) => handleSettingUpdate('defaultLanguage', e.target.value)}
              label="Default Language"
            >
              {languageOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size={isMobile ? "small" : "medium"}>
            <InputLabel>Date Format</InputLabel>
            <Select
              value={defaultSettings.dateFormat || 'MM/DD/YYYY'}
              onChange={(e) => handleSettingUpdate('dateFormat', e.target.value)}
              label="Date Format"
            >
              <MenuItem value="MM/DD/YYYY">MM/DD/YYYY (US)</MenuItem>
              <MenuItem value="DD/MM/YYYY">DD/MM/YYYY (UK)</MenuItem>
              <MenuItem value="YYYY-MM-DD">YYYY-MM-DD (ISO)</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size={isMobile ? "small" : "medium"}>
            <InputLabel>Time Format</InputLabel>
            <Select
              value={defaultSettings.timeFormat || '12'}
              onChange={(e) => handleSettingUpdate('timeFormat', e.target.value)}
              label="Time Format"
            >
              <MenuItem value="12">12 Hour (AM/PM)</MenuItem>
              <MenuItem value="24">24 Hour</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )
    },
    {
      id: 'receipt',
      label: 'Receipt Settings',
      icon: <ReceiptIcon />,
      color: '#8b5cf6',
      content: (
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={defaultSettings.printReceiptAutomatically || false}
                onChange={(e) => handleSettingUpdate('printReceiptAutomatically', e.target.checked)}
                color="primary"
              />
            }
            label="Print Receipt Automatically"
          />

          <FormControlLabel
            control={
              <Switch
                checked={defaultSettings.showOrderNumber || true}
                onChange={(e) => handleSettingUpdate('showOrderNumber', e.target.checked)}
                color="primary"
              />
            }
            label="Show Order Number"
          />

          <FormControlLabel
            control={
              <Switch
                checked={defaultSettings.showItemDetails || true}
                onChange={(e) => handleSettingUpdate('showItemDetails', e.target.checked)}
                color="primary"
              />
            }
            label="Show Item Details"
          />

          <TextField
            fullWidth
            label="Receipt Footer Message"
            value={defaultSettings.receiptFooter || 'Thank you for your business!'}
            onChange={(e) => handleSettingUpdate('receiptFooter', e.target.value)}
            size={isMobile ? "small" : "medium"}
            multiline
            rows={2}
            helperText="Message shown at bottom of receipt"
          />
        </Stack>
      )
    }
  ];

  return (
    <Box>
      {/* ✅ COMPACT: Header */}
      <Box sx={{ mb: isMobile ? 2 : 3 }}>
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
          {t('posConfig.defaultSettings.title') || 'Default Settings'}
        </Typography>
        <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
          {t('posConfig.defaultSettings.description') || 'Configure global settings for your POS system'}
        </Typography>
      </Box>

      {/* ✅ COMPACT: Info Alert */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: isMobile ? 2 : 3,
          borderRadius: 2,
          fontSize: isMobile ? '0.8rem' : '0.875rem'
        }}
      >
        {t('posConfig.defaultSettings.infoAlert') || 'These settings will be applied across your entire POS system and affect how prices and information are displayed.'}
      </Alert>

      {/* ✅ MOBILE: Accordion Layout */}
      {isMobile ? (
        <Stack spacing={1}>
          {settingSections.map((section, index) => (
            <MotionAccordion
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              sx={{
                background: 'white',
                border: `1px solid ${section.color}20`,
                borderLeft: `4px solid ${section.color}`,
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                '&.Mui-expanded': {
                  boxShadow: `0 4px 16px ${section.color}25`
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  py: 1,
                  '& .MuiAccordionSummary-content': { 
                    alignItems: 'center',
                    my: 1
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: `${section.color}15`, color: section.color }}>
                    {React.cloneElement(section.icon, { sx: { fontSize: 16 } })}
                  </Avatar>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333' }}>
                    {section.label}
                  </Typography>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                {section.content}
              </AccordionDetails>
            </MotionAccordion>
          ))}
        </Stack>
      ) : (
        /* ✅ TABLET/DESKTOP: Tabbed Layout */
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          <Box sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
            <Tabs
              value={currentTab}
              onChange={(e, newValue) => setCurrentTab(newValue)}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  minHeight: 60,
                  color: '#64748b',
                  '&:hover': {
                    color: '#667eea',
                    bgcolor: 'rgba(102, 126, 234, 0.04)'
                  },
                  '&.Mui-selected': {
                    color: '#667eea'
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#667eea',
                  height: 3
                }
              }}
            >
              {settingSections.map((section, index) => (
                <Tab
                  key={section.id}
                  icon={
                    <Avatar sx={{ 
                      width: 24, 
                      height: 24, 
                      bgcolor: currentTab === index ? `${section.color}15` : 'transparent',
                      color: currentTab === index ? section.color : '#64748b',
                      mb: 0.5
                    }}>
                      {React.cloneElement(section.icon, { sx: { fontSize: 14 } })}
                    </Avatar>
                  }
                  label={section.label}
                  iconPosition="top"
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ p: isTablet ? 2 : 3, minHeight: 300 }}>
            {settingSections.map((section, index) => (
              <TabPanel key={section.id} value={currentTab} index={index}>
                <MotionCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  sx={{
                    border: `1px solid ${section.color}20`,
                    borderRadius: 3,
                    boxShadow: 'none'
                  }}
                >
                  <CardContent sx={{ p: isTablet ? 2 : 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Avatar sx={{ width: 40, height: 40, bgcolor: `${section.color}15`, color: section.color }}>
                        {section.icon}
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: section.color }}>
                        {section.label}
                      </Typography>
                    </Box>
                    {section.content}
                  </CardContent>
                </MotionCard>
              </TabPanel>
            ))}
          </Box>
        </Paper>
      )}

      {/* ✅ COMPACT: Current Settings Summary */}
      <Paper sx={{ 
        mt: isMobile ? 3 : 4, 
        p: isMobile ? 2 : 3,
        bgcolor: 'success.lighter',
        borderRadius: 3,
        border: '1px solid rgba(76, 175, 80, 0.1)'
      }}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, mb: 2, color: 'success.main' }}>
          {t('posConfig.defaultSettings.currentSummary') || 'Current Settings Summary'}
        </Typography>
        
        <Grid container spacing={isMobile ? 1 : 2}>
          <Grid item xs={12} sm={6}>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<CurrencyIcon />}
                  label={`${defaultSettings.currencySymbol || '$'} (${defaultSettings.decimalPlaces || 2} decimals)`}
                  size="small"
                  sx={{ bgcolor: '#10b98115', color: '#10b981', fontWeight: 600 }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<PercentIcon />}
                  label={`Tax: ${defaultSettings.defaultTaxRate || 0}%${defaultSettings.includeTaxInPrice ? ' (included)' : ' (added)'}`}
                  size="small"
                  sx={{ bgcolor: '#f59e0b15', color: '#f59e0b', fontWeight: 600 }}
                />
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<LanguageIcon />}
                  label={languageOptions.find(l => l.value === (defaultSettings.defaultLanguage || 'en'))?.label || 'English'}
                  size="small"
                  sx={{ bgcolor: '#667eea15', color: '#667eea', fontWeight: 600 }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                  <strong>Date Format:</strong> {defaultSettings.dateFormat || 'MM/DD/YYYY'}
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default DefaultSettingsManager;